/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useEffect, useRef, useState } from "react";
import { BellSlashIcon } from "@heroicons/react/24/outline";

const CHOICES: { label: string; seconds: number }[] = [
  { label: "For a day", seconds: 86_400 },
  { label: "For a week", seconds: 7 * 86_400 },
  { label: "Forever", seconds: 0 },
];

/** Small "mute this problem" control: a bell button that opens the duration choices inline. */
export function MuteButton({ onMute }: { onMute: (seconds: number) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  // the list scrolls, so the choices open inline instead of in a floating menu
  return (
    <span className="cc-mute" ref={ref}>
      {open ? (
        <span className="cc-mute-choices" role="menu">
          {CHOICES.map((c) => (
            <button
              key={c.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onMute(c.seconds);
              }}
            >
              {c.label}
            </button>
          ))}
        </span>
      ) : (
        <button type="button" className="cc-mute-btn" aria-label="Mute" title="Mute" onClick={() => setOpen(true)}>
          <BellSlashIcon className="h-4 w-4" />
        </button>
      )}
    </span>
  );
}
